'use client';

import React from 'react';
import { Button, ImageRotator } from '@ui';
import { IconArrowRight } from '@icons';

export type HomeProjectImage = {
  image_url: string;
  image_url_2x?: string;
  alt?: string;
};

export type HomeProjectEntry = {
  id?: number;
  project_id?: number;
  isFeatured?: boolean;
  /** When true, the project stays in locale data but is not rendered. */
  hidden?: boolean;
  title?: string;
  description?: string;
  summary?: string;
  meta_role?: string;
  meta_type?: string;
  meta_impact?: string;
  siteUrl?: string;
  target?: string;
  rel?: string;
  /** Button label; when set, overrides the section default CTA text. */
  appLink?: string;
  project_images?: HomeProjectImage[];
};

export function ctaLabelForProject(project: HomeProjectEntry, fallback: string): string {
  const custom = project.appLink;
  if (typeof custom === 'string' && custom.trim() !== '') {
    return custom.trim();
  }
  return fallback;
}

type HomeProjectCardProps = {
  project: HomeProjectEntry;
  featured?: boolean;
  ctaLabel: string;
};

function toRotatorImages(images: HomeProjectImage[]) {
  return images
    .filter((img) => typeof img.image_url === 'string' && img.image_url.trim().length > 0)
    .map((img) => {
      const src2x =
        typeof img.image_url_2x === 'string' && img.image_url_2x.trim().length > 0
          ? img.image_url_2x.trim()
          : undefined;
      return {
        src: img.image_url.trim(),
        ...(src2x ? { src2x } : {}),
        alt: img.alt,
      };
    });
}

const HomeProjectCard: React.FC<HomeProjectCardProps> = ({
  project,
  featured = false,
  ctaLabel,
}) => {
  const p = project;
  const images = toRotatorImages(p.project_images ?? []);
  const href = p.siteUrl ?? '#';
  const target = p.target?.trim() || undefined;
  const rel = p.rel?.trim() || (target === '_blank' ? 'noopener noreferrer' : undefined);
  const summary = (p.summary ?? '').trim() || (p.description ?? '').trim();
  const metaLines = [p.meta_role, p.meta_type, p.meta_impact].filter(
    (line): line is string => typeof line === 'string' && line.trim().length > 0
  );

  return (
    <article className={`card project-card${featured ? ' featured' : ''}`}>
      <div className='card-layout'>
        <div className='copy'>
          {p.title ? <h3 className='title'>{p.title}</h3> : null}
          {metaLines.length > 0 ? (
            <ul className='meta' aria-label='Project context'>
              {metaLines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          ) : null}
          {summary ? <p className='summary'>{summary}</p> : null}
          <Button
            as='a'
            href={href}
            label={ctaLabel}
            className='btn-primary'
            icon={<IconArrowRight />}
            iconPosition='right'
            target={target}
            rel={rel}
          />
        </div>
        {images.length > 0 ? (
          <div className='media'>
            <div className='media-frame'>
              <ImageRotator
                className='image-rotator'
                images={images}
                interval={3000}
                width='100%'
                height='100%'
                transition='slide'
              />
            </div>
          </div>
        ) : null}
      </div>
    </article>
  );
};

export default HomeProjectCard;
